import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // for grid layout in jsPDF
import { useNotification } from '../../context/NotificationContext';

import GridItem from './GridItem';
import { isEnhancedCell } from '../../utils/productBlockLayout';

const CELL_SIZE_REDUCTION_FACTOR = 0.25;
const CELL_SIZE = 100; // The size of each cell in the grid

// Resolution multiplier for high-quality exports (5x for 5x zoom without pixelation)
const EXPORT_RESOLUTION_MULTIPLIER = 5;
const BASE_EXPORT_WIDTH = 1200;
const BASE_EXPORT_HEIGHT = 1600;
const BASE_HORIZONTAL_MARGIN = 15;
const BASE_VERTICAL_MARGIN = 20;

const Flyer = forwardRef(function Flyer({ templateId, createdFlyer, setCreatedFlyer, style, showCenterGuides = false, currentPageIndex = null, onPageCountChange = null, selectedCellProduct, onSelectCellProduct, onReplaceCellProduct }, ref) {
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();
  // eslint-disable-next-line no-unused-vars
  const [shapesLayerRefs, setShapesLayerRefs] = useState({}); // State triggers re-renders, ref is used for reading
  const shapesLayerRefsRef = useRef({}); // Ref to always have latest refs for export
  const initialUpdateRef = useRef(true);
  const [nextProductSlots, setNextProductSlots] = useState([new Array(16).fill('1')]);
  const navigate = useNavigate();
  const [grids, setGrids] = useState([Array(12).fill(null)]);
  const gridsRef = useRef(grids);
  gridsRef.current = grids;
  const [gridDimensions, setGridDimensions] = useState(['4x3']);
  const [isExporting, setIsExporting] = useState(false);
  function showOverlay() {
      const overlay = document.querySelector('.overlay');
      if (overlay) overlay.style.display = 'flex';
  }

  function hideOverlay() {
      const overlay = document.querySelector('.overlay');
      if (overlay) overlay.style.display = 'none';
  }

  // Moved template assignment logic to its own function
  const assignTemplate = async (selectedTemplate) => {
    if (!selectedTemplate) {
      alert('No template selected');
      return;
    }
    const token = await getAccessTokenSilently();
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_DOMAIN}/api/flyertemplates/${selectedTemplate}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (response.data && response.data.data) {
      const flyerTemplateData = JSON.parse(response.data.data);
      if(flyerTemplateData) {

       // Merge enhanced layer shapes into content for each cell
       const mergedData = flyerTemplateData.map(page =>
         page.map(cell => {
           if (cell && (cell.static_shapes || cell.shared_shapes || cell.product_block)) {
             const staticS = cell.static_shapes ? (typeof cell.static_shapes === 'string' ? JSON.parse(cell.static_shapes) : cell.static_shapes) : [];
             const sharedS = cell.shared_shapes ? (typeof cell.shared_shapes === 'string' ? JSON.parse(cell.shared_shapes) : cell.shared_shapes) : [];
             const productS = cell.product_block?.shapes ? (typeof cell.product_block.shapes === 'string' ? JSON.parse(cell.product_block.shapes) : cell.product_block.shapes) : [];
             const merged = [...staticS, ...sharedS, ...productS];
             if (merged.length > 0) cell.content = merged;
           }
           return cell;
         })
       );
       setGrids(mergedData || [Array(12).fill(null)]);

      // Determine nextProductSlots for each grid_data element
      const nextSlots = flyerTemplateData.map(grid => {
        if (grid.length > 12) {
          return Array(16).fill('1');
        } else {
          return Array(12).fill('1');
        }
      });
      setNextProductSlots(nextSlots);

      const dimensions = flyerTemplateData.map(grid => {
        if (grid.length > 12) {
          return '4x4';
        } else {
          return '4x3';
        }
      });
      setGridDimensions(dimensions);
      }
    } else {
      console.error(`No template found with ID: ${selectedTemplate}`);
      // Handle error accordingly
    }
  };

  useEffect(() => {
    if (templateId) {
        assignTemplate(templateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  useEffect(() => {
    if (initialUpdateRef.current) {
      if(createdFlyer.grid_data){
        // Merge enhanced layer shapes into content for each cell
        const mergedData = createdFlyer.grid_data.map(page =>
          page.map(cell => {
            if (cell && (cell.static_shapes || cell.shared_shapes || cell.product_block)) {
              const staticS = cell.static_shapes ? (typeof cell.static_shapes === 'string' ? JSON.parse(cell.static_shapes) : cell.static_shapes) : [];
              const sharedS = cell.shared_shapes ? (typeof cell.shared_shapes === 'string' ? JSON.parse(cell.shared_shapes) : cell.shared_shapes) : [];
              const productS = cell.product_block?.shapes ? (typeof cell.product_block.shapes === 'string' ? JSON.parse(cell.product_block.shapes) : cell.product_block.shapes) : [];
              const merged = [...staticS, ...sharedS, ...productS];
              if (merged.length > 0) cell.content = merged;
            }
            return cell;
          })
        );
        setGrids(mergedData || [Array(12).fill(null)]);

      // Determine nextProductSlots for each grid_data element
      const nextSlots = createdFlyer.grid_data.map(grid => {
        if (grid.length > 12) {
          return Array(16).fill('1');
        } else {
          return Array(12).fill('1');
        }
      });
      setNextProductSlots(nextSlots);

      const dimensions = createdFlyer.grid_data.map(grid => {
        if (grid.length > 12) {
          return '4x4';
        } else {
          return '4x3';
        }
      });
      setGridDimensions(dimensions);
      } else {
        assignTemplate(createdFlyer.template_id);
      }
      initialUpdateRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdFlyer]);

  const handleCellUpdate = useCallback((index, updatedCell, extraProps) => {
    setGrids(prevGrids => {
        const [updatePageNumberString, updateIndexString] = index.split('-');
        const updatePageNumber = parseInt(updatePageNumberString);
        const updateIndex = parseInt(updateIndexString);
        const updatedGrids = [...prevGrids];
        const currentGrid = [...updatedGrids[updatePageNumber]];
        // Create a NEW cell object (not mutate) so React detects the prop change
        currentGrid[updateIndex] = {
          ...currentGrid[updateIndex],
          content: updatedCell,
          ...extraProps,
        };
        updatedGrids[updatePageNumber] = currentGrid;
        return updatedGrids;
    });
  }, []);

  // Handle cell override (brand ad / offer)
  const handleCellOverride = useCallback((index, overrideData) => {
    setGrids(prevGrids => {
      const [pageStr, cellStr] = index.split('-');
      const pageNum = parseInt(pageStr);
      const cellIdx = parseInt(cellStr);
      const updatedGrids = [...prevGrids];
      const currentGrid = [...updatedGrids[pageNum]];

      if (overrideData.cellMode === 'product') {
        // Clear override — restore to product mode
        delete currentGrid[cellIdx].cellMode;
        delete currentGrid[cellIdx].overrideType;
        delete currentGrid[cellIdx].overrideContent;
      } else {
        // Set override
        currentGrid[cellIdx].cellMode = overrideData.cellMode;
        currentGrid[cellIdx].overrideType = overrideData.overrideType;
        currentGrid[cellIdx].overrideContent = overrideData.overrideContent;
      }

      updatedGrids[pageNum] = currentGrid;
      return updatedGrids;
    });
  }, []);

  // Delete a product from an enhanced cell by index
  const handleDeleteCellProduct = useCallback((cellIndex, productIndex) => {
    setGrids(prevGrids => {
      const [pageStr, cellStr] = cellIndex.split('-');
      const pageNum = parseInt(pageStr);
      const cellIdx = parseInt(cellStr);
      const updatedGrids = [...prevGrids];
      const currentGrid = [...updatedGrids[pageNum]];
      const cellObj = currentGrid[cellIdx];
      if (cellObj?.products) {
        const newProducts = [...cellObj.products];
        newProducts.splice(productIndex, 1);
        currentGrid[cellIdx] = { ...cellObj, products: newProducts };
        updatedGrids[pageNum] = currentGrid;
      }
      return updatedGrids;
    });
    // Clear selection after delete
    if (onSelectCellProduct) onSelectCellProduct(null, null);
  }, [onSelectCellProduct]);

  useEffect(() => {
    setCreatedFlyer(prevFlyer => ({
      ...prevFlyer,
      grid_data: grids
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grids]);

  // Notify parent of page count changes
  useEffect(() => {
    if (onPageCountChange) {
      onPageCountChange(grids.length);
    }
  }, [grids.length, onPageCountChange]);

  const generatePreviewImageBlob = async (shapesLayerRefs, grids, gridDimensions) => {
    // Generate the image data for page 1
    const { imageUrls } = await generateImageData(shapesLayerRefs);
    
    return new Promise((resolve, reject) => {
      const pageNumber = 0; // Assuming page numbers are string type
      const pageImages = imageUrls.filter(img => img.pageNumber === pageNumber);
  
      const currentPageDimensions = gridDimensions[pageNumber];
      let numCols = currentPageDimensions ? parseInt(currentPageDimensions.split('x')[1]) : 3;
      // Preview uses base resolution (no multiplier needed for preview thumbnails)
      const pageWidth = BASE_EXPORT_WIDTH;
      const pageHeight = BASE_EXPORT_HEIGHT;
      const horizontalMargin = BASE_HORIZONTAL_MARGIN;
      const verticalMargin = BASE_VERTICAL_MARGIN;

      const numRows = 4;
      const cellWidth = (pageWidth - 2 * horizontalMargin) / numCols;
      const cellHeight = (pageHeight - 2 * verticalMargin) / numRows;

      const canvas = document.createElement('canvas');
      canvas.width = BASE_EXPORT_WIDTH;
      canvas.height = BASE_EXPORT_HEIGHT;
      const ctx = canvas.getContext('2d');

  
      const imageLoadPromises = pageImages.map(imageObj => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            let imgWidth, imgHeight;
            if (img.width > img.height) {
              imgWidth = cellWidth * grids[imageObj.pageNumber][imageObj.idxNumber].spanWidth;
              imgHeight = imgWidth / aspectRatio;
            } else {
              imgHeight = cellHeight * grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight;
              imgWidth = imgHeight * aspectRatio;
            }
  
            const img_col = Math.floor(imageObj.idxNumber % numCols);
            const img_row = Math.floor(imageObj.idxNumber / numCols);
            const img_x = img_col * cellWidth + horizontalMargin;
            const img_y = img_row * cellHeight + verticalMargin;

            ctx.drawImage(img, img_x, img_y, imgWidth, imgHeight);
            resolve();
          };
          img.src = URL.createObjectURL(imageObj.blob);
        });
      });
  
      Promise.all(imageLoadPromises).then(() => {
        canvas.toBlob(blob => {
          resolve(blob);
        }, 'image/png');
      }).catch((error) => {
        reject(error);
      });
    });
  };  

  const handleFlyerPreviewImageUpload = (file, fileName) => {
    return new Promise(async (resolve, reject) => {
      const token = await getAccessTokenSilently();
     
      if (!file) {
        resolve(null);
        return;
      }
    
      const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/s3/signed-url?type=template&fileName=${encodeURIComponent(fileName)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const { data: { signedUrl } } = response;
      if (signedUrl) {
        const result = await axios.put(signedUrl, file, {
          headers: {
            'Content-Type': file.type
          }
        });
    
        if (result.status === 200) {
          const imageUrl = signedUrl.split("?")[0];
          resolve(imageUrl); // Resolve the promise with imageUrl
        } else {
          console.error('Error uploading file:', result);
          reject('Error uploading file.');
        }
      } else {
        console.error('Server did not return a signed URL for upload.');
        reject('Server did not return a signed URL for upload.');
      }
    });
  };
  
  const getMultipleSignedUrls = async (files) => {
    const token = await getAccessTokenSilently();

    // Construct the payload as an array of objects.
    const payload = files.map(file => ({
        fileName: file.fileName,
        fileType: file.fileType // 'image/png' or 'application/pdf' etc.
    }));

    const response = await axios.post(`${process.env.REACT_APP_SERVER_DOMAIN}/api/s3/multiple-signed-urls`, {
        files: payload
        }, 
        {
        headers: {
            Authorization: `Bearer ${token}`,
        }
    });
    // Expecting response.data to be an array of {signedUrl, fileName} objects.
    return response.data;
  };
  
  const uploadMultipleToS3 = async (signedUrlsData, files) => {
    const uploadPromises = signedUrlsData.map((data, index) => {
        const blob = files[index].blob;
        return axios.put(data.signedUrl, blob, {
            headers: {
                'Content-Type': blob.type
            }
        }).then(() => {
            return { originalName: files[index].fileName, url: data.signedUrl.split("?")[0] };
        });
    });

    const results = await Promise.all(uploadPromises);
    return results;
  };

  const uploadFiles = (files) => {
    return new Promise(async (resolve, reject) => {
        try {
            const signedUrlsData = await getMultipleSignedUrls(files);
            //const blobs = files.map(file => file.blob);
            const uploadedFilesData = await uploadMultipleToS3(signedUrlsData, files);

            resolve(uploadedFilesData);
            notification.success('Flyer changes have been successfully published.');
        } catch (error) {
            notification.error('Error occurred publishing the flyer.');
            reject(error);
        }
    });
  };

  const generateImageData = (shapesLayerRefs) => {
    const imagePromises = [];
    const imageUrls = [];
    const fileData = [];

    for (const [index, stageRef] of Object.entries(shapesLayerRefs)) {
      if (stageRef) {
          const validIndexPattern = /^\d+-\d+$/;
          if (!validIndexPattern.test(index)) {
              console.warn(`Invalid ref index: ${index}`);
              continue;
          }

          const imagePromise = new Promise((resolve, reject) => {
              stageRef.toImage({
                  callback: function(img) {
                      const [pageNumber, idxNumber] = index.split('-').map(num => parseInt(num));
                      const canvas = document.createElement('canvas');
                      canvas.width = img.width;
                      canvas.height = img.width * (grids[pageNumber][idxNumber].dimensions.height / grids[pageNumber][idxNumber].dimensions.width);
                      const ctx = canvas.getContext('2d');
                      ctx.drawImage(img, 0, 0);
                      canvas.toBlob((blob) => {
                          if (!blob) {
                              console.error(`Failed to convert image ${index} to blob`);
                              reject(new Error(`Failed to convert image ${index} to blob`));
                              return;
                          }
                          const url = URL.createObjectURL(blob);
                          const imageObj = {
                              pageNumber: pageNumber,
                              idxNumber: idxNumber,
                              url: url,
                              blob: blob,
                              fileName: `Flyer_${index}.png`,
                              fileType: 'image/png'
                          };
                          imageUrls.push(imageObj);
                          fileData.push(imageObj);
                          resolve(url);
                      }, 'image/png');
                  },
                  error: function(err) {
                      console.error(`Image creation for layer ${index} failed with error:`, err);
                      reject(err);
                  },
                  mimeType: 'image/png',
                  quality: 1,
                  pixelRatio: 7,
              });
          });
          imagePromises.push(imagePromise);
      }
  }

  return Promise.all(imagePromises).then(() => {
      return { imageUrls, fileData };
  });
  };
  
  const saveFlyer = async (callFromSaveButton = true) => {
    if(callFromSaveButton) {
      showOverlay();
    }

    try {
      // Parent component (AddFlyer) should switch to page 0 before calling saveFlyer
      // to ensure the preview image is generated from page 0
      // Give a moment for any pending renders to complete
      await new Promise(r => setTimeout(r, 100));

      // Save that image to the server as previewImage (uses current page - should be page 0)
      const previewImageBlob = await generatePreviewImageBlob(shapesLayerRefsRef.current, grids, gridDimensions);
      const flyerId = createdFlyer._id || createdFlyer.flyer_id; // MongoDB uses _id
      const previewImageFileName = `preview_${flyerId}.png`; // Ensure to have a valid ID or name here
      const previewImageUrl = await handleFlyerPreviewImageUpload(previewImageBlob, previewImageFileName);

      // Add the URL to createdFlyer
      createdFlyer.preview_image_url = previewImageUrl;

      // Update the flyer object in the backend
      const token = await getAccessTokenSilently();
      const response = await axios.patch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/flyers/${flyerId}`,
        JSON.stringify(createdFlyer),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const startDate = new Date(response.data.result.start_date);
      const endDate = new Date(response.data.result.end_date);

      setCreatedFlyer((prevFlyer) => ({
        ...prevFlyer,
        ...response.data.result,
        start_date: startDate,
        end_date: endDate,
      }));

      if(callFromSaveButton) {
        hideOverlay();
        notification.success('Flyer has been successfully updated.');
      }
    } catch (error) {
      if(callFromSaveButton) {
        hideOverlay();
      }
      console.error('Error occurred while saving the flyer:', error);
      notification.error('Error occurred while saving the flyer.');
    }
  };

  function formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthName = monthNames[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    return `${day}_${monthName}_${year}`;
  }

  const downloadFlyer = async () => {
    showOverlay();

    await saveFlyer(false);

    // Enable export mode to render ALL pages (not just current page)
    // This ensures all GridItem refs are registered before generating images
    setIsExporting(true);

    // Wait for React to re-render all pages and register their refs
    // Calculate expected number of cells across all pages
    const expectedCellCount = grids.reduce((total, grid) => {
      if (!grid) return total;
      return total + grid.filter(cell => cell !== null).length;
    }, 0);

    // Wait for all refs to be registered (with timeout to prevent infinite wait)
    // We need to wait for the DOM to update and refs to be registered
    await new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      const checkRefs = () => {
        // Query the DOM for rendered grid items to check if all pages are rendered
        const renderedGridItems = document.querySelectorAll('.grid-item');
        if (renderedGridItems.length >= expectedCellCount || attempts >= maxAttempts) {
          // Additional delay to ensure refs are registered after DOM elements exist
          setTimeout(resolve, 200);
        } else {
          attempts++;
          setTimeout(checkRefs, 100);
        }
      };
      // Initial delay to allow React to start re-rendering
      setTimeout(checkRefs, 100);
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imagePromises = [];

    // Use ref instead of state to get the latest refs after re-render
    const { imageUrls, fileData } = await generateImageData(shapesLayerRefsRef.current);

    // Generate a unique folder name
    const flyerName = createdFlyer.flyer_title; // Replace with actual flyer name
    const startDate = new Date(createdFlyer.start_date); // Replace with actual start date
    const formattedStartDate = formatDate(startDate);
    const mainFolderName = `${flyerName}_${formattedStartDate}_${Date.now()}`;

    Promise.all(imagePromises)
        .then(() => {
          const horizontalMargin = 15;
          const verticalMargin = 20;
          // const pageWidth = pdf.internal.pageSize.getWidth();
          // const pageHeight = pdf.internal.pageSize.getHeight();
          
          const pageWidth = 210;
          const pageHeight = 280;

          let p = Promise.resolve();
            imageUrls.sort((a, b) => {
                if (a.pageNumber === b.pageNumber) {
                    return a.idxNumber - b.idxNumber;
                }
                return a.pageNumber - b.pageNumber;
            });

            let currentRow = 0;
            let currentCol = 0;
            let currentPage = -1;
            let currentRowMinHeight = 0;

            imageUrls.forEach((imageObj, absoluteIndex) => {
                if (imageObj.pageNumber !== currentPage) {
                    currentPage = imageObj.pageNumber;
                    currentRow = 0;
                    currentCol = 0;
                    currentRowMinHeight = 0;
                }

                const currentPageDimensions = gridDimensions[imageObj.pageNumber];
                const numCols = currentPageDimensions ? parseInt(currentPageDimensions.split('x')[1]) : 3;
                const numRows = 4;
                const cellWidth = (pageWidth - 2 * horizontalMargin) / numCols;
                const cellHeight = (pageHeight - 2 * verticalMargin) / numRows;

              
                p = p.then(() => {
                    return new Promise((resolve, reject) => {
                        const img = new Image();
                        img.onload = function() {
                            const aspectRatio = this.width / this.height;
                            let imgWidth, imgHeight;

                            if (this.width > this.height) {
                                imgWidth = cellWidth * grids[imageObj.pageNumber][imageObj.idxNumber].spanWidth;
                                imgHeight = imgWidth / aspectRatio;
                            } else {
                                imgHeight = cellHeight * grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight;
                                imgWidth = imgHeight * aspectRatio;
                            }

                            const img_col = Math.floor(imageObj.idxNumber % numCols);
                            const img_row = Math.floor(imageObj.idxNumber / numCols);
                            const img_x = img_col * cellWidth + horizontalMargin;
                            const img_y = img_row * cellHeight + ((1.5) * verticalMargin);


                            pdf.addImage(imageObj.url, 'PNG', img_x, img_y, imgWidth, imgHeight);

                            if (currentCol === 0) {
                                currentRowMinHeight = grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight;
                            } else {
                                currentRowMinHeight = Math.min(currentRowMinHeight, grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight);
                            }
                            currentCol = Math.floor(imageObj.idxNumber % numCols) + grids[imageObj.pageNumber][imageObj.idxNumber].spanWidth;
                            if (currentCol >= numCols) {
                                currentRow += currentRowMinHeight;
                                currentCol = 0;
                            }

                            if (currentRow >= numRows && absoluteIndex !== imageUrls.length - 1) {
                                pdf.addPage();
                                currentRow = 0;
                                currentCol = 0;
                            }

                            resolve();
                        };

                        img.onerror = (errorEvent) => {
                            console.error("Error loading image:", errorEvent);
                            reject(new Error(`Failed to load image at URL: ${imageObj.url}`));
                        };
                        img.src = imageObj.url;
                    });
                });
            });
            return p;
        })
        .then(() => {
            pdf.save(`${mainFolderName}/_for_web/final_flyer.pdf`);
            imageUrls.forEach((imageObj) => URL.revokeObjectURL(imageObj.url));

            const pages = {};
            fileData.forEach(fileObj => {
                if (!pages[fileObj.pageNumber]) {
                    pages[fileObj.pageNumber] = [];
                }
                pages[fileObj.pageNumber].push(fileObj);
            });

            return Promise.all(Object.entries(pages).map(([pageNumber, pageImages]) => {
                return new Promise((resolve) => {
                    pageImages.sort((a, b) => a.idxNumber - b.idxNumber);

                    const currentPageDimensions = gridDimensions[pageNumber];
                    let numCols = currentPageDimensions ? parseInt(currentPageDimensions.split('x')[1]) : 3;

                    // High-resolution export for download (5x base resolution)
                    const pageWidth = BASE_EXPORT_WIDTH * EXPORT_RESOLUTION_MULTIPLIER;
                    const pageHeight = BASE_EXPORT_HEIGHT * EXPORT_RESOLUTION_MULTIPLIER;

                    const horizontalMargin = BASE_HORIZONTAL_MARGIN * EXPORT_RESOLUTION_MULTIPLIER;
                    const verticalMargin = BASE_VERTICAL_MARGIN * EXPORT_RESOLUTION_MULTIPLIER;

                    const numRows = 4;
                    const cellWidth = (pageWidth - 2 * horizontalMargin) / numCols;
                    const cellHeight = (pageHeight - 2 * verticalMargin) / numRows;

                    const canvas = document.createElement('canvas');
                    canvas.width = BASE_EXPORT_WIDTH * EXPORT_RESOLUTION_MULTIPLIER;
                    canvas.height = BASE_EXPORT_HEIGHT * EXPORT_RESOLUTION_MULTIPLIER;
                    const ctx = canvas.getContext('2d');

                    let localRow = 0;
                    let currentCol = 0;
                    let currentRowMinHeight = 0;

                    const imageLoadPromises = pageImages.map(imageObj => {
                        return new Promise((resolve) => {
                            const img = new Image();
                            img.onload = () => {
                                const aspectRatio = img.width / img.height;
                                let imgWidth, imgHeight;
                                if (img.width > img.height) {
                                    imgWidth = cellWidth * grids[imageObj.pageNumber][imageObj.idxNumber].spanWidth;
                                    imgHeight = imgWidth / aspectRatio;
                                } else {
                                    imgHeight = cellHeight * grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight;
                                    imgWidth = imgHeight * aspectRatio;
                                }

                                const img_col = Math.floor(imageObj.idxNumber % numCols);
                                const img_row = Math.floor(imageObj.idxNumber / numCols);
                                const img_x = img_col * cellWidth + horizontalMargin;
                                const img_y = img_row * cellHeight + verticalMargin;
                                
                                ctx.drawImage(img, img_x, img_y, imgWidth, imgHeight);
                                if (currentCol === 0) {
                                    currentRowMinHeight = grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight;
                                } else {
                                    currentRowMinHeight = Math.min(currentRowMinHeight, grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight);
                                }
                                currentCol += grids[imageObj.pageNumber][imageObj.idxNumber].spanWidth;
                                if (currentCol >= numCols) {
                                    localRow += currentRowMinHeight;
                                    currentCol = 0;
                                }
                                void localRow;
                                resolve();
                            };
                            img.src = URL.createObjectURL(imageObj.blob);
                        });
                    });

                    Promise.all(imageLoadPromises).then(() => {
                        canvas.toBlob(blob => {
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            let printPageNumber = parseInt(pageNumber) + 1;
                            link.download = `${mainFolderName}/_for_social_ads/flyer_page_${(printPageNumber)}.png`;
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            resolve();
                        }, 'image/png');
                    });
                });
            }));
        })
        .then(() => {
            setIsExporting(false);
            hideOverlay();
            notification.success('All Flyer files have been successfully downloaded.');
        })
        .catch((error) => {
            console.error("Error:", error);
            setIsExporting(false);
            hideOverlay();
        });
  };

  // eslint-disable-next-line no-unused-vars
  const publishFlyer = async () => {
    showOverlay();

    await saveFlyer(false);

    // Enable export mode to render ALL pages
    setIsExporting(true);

    // Wait for all pages to render
    const expectedCellCount = grids.reduce((total, grid) => {
      if (!grid) return total;
      return total + grid.filter(cell => cell !== null).length;
    }, 0);

    await new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 50;
      const checkRefs = () => {
        const renderedGridItems = document.querySelectorAll('.grid-item');
        if (renderedGridItems.length >= expectedCellCount || attempts >= maxAttempts) {
          setTimeout(resolve, 200);
        } else {
          attempts++;
          setTimeout(checkRefs, 100);
        }
      };
      setTimeout(checkRefs, 100);
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imagePromises = [];
    const {imageUrls, fileData } = await generateImageData(shapesLayerRefsRef.current);

    Promise.all(imagePromises)
        .then(() => {
            const pages = {};
            fileData.forEach(fileObj => {
                if (!pages[fileObj.pageNumber]) {
                    pages[fileObj.pageNumber] = [];
                }
                pages[fileObj.pageNumber].push(fileObj);
            });

            return Promise.all(Object.entries(pages).map(([pageNumber, pageImages]) => {
                return new Promise((resolve) => {
                    pageImages.sort((a, b) => a.idxNumber - b.idxNumber);

                    const currentPageDimensions = gridDimensions[pageNumber];
                    const numCols = currentPageDimensions ? parseInt(currentPageDimensions.split('x')[1]) : 3;
                    // High-resolution export for publish (5x base resolution)
                    const pageWidth = BASE_EXPORT_WIDTH * EXPORT_RESOLUTION_MULTIPLIER;
                    const pageHeight = BASE_EXPORT_HEIGHT * EXPORT_RESOLUTION_MULTIPLIER;
                    const horizontalMargin = BASE_HORIZONTAL_MARGIN * EXPORT_RESOLUTION_MULTIPLIER;
                    const verticalMargin = BASE_VERTICAL_MARGIN * EXPORT_RESOLUTION_MULTIPLIER;
                    const numRows = 4;
                    const cellWidth = (pageWidth - 2 * horizontalMargin) / numCols;
                    const cellHeight = (pageHeight - 2 * verticalMargin) / numRows;

                    const canvas = document.createElement('canvas');
                    canvas.width = BASE_EXPORT_WIDTH * EXPORT_RESOLUTION_MULTIPLIER;
                    canvas.height = BASE_EXPORT_HEIGHT * EXPORT_RESOLUTION_MULTIPLIER;
                    const ctx = canvas.getContext('2d');

                    const imageLoadPromises = pageImages.map(imageObj => {
                        return new Promise((resolve) => {
                            const img = new Image();
                            img.onload = () => {
                                const aspectRatio = img.width / img.height;
                                let imgWidth, imgHeight;
                                if (img.width > img.height) {
                                    imgWidth = cellWidth * grids[imageObj.pageNumber][imageObj.idxNumber].spanWidth;
                                    imgHeight = imgWidth / aspectRatio;
                                } else {
                                    imgHeight = cellHeight * grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight;
                                    imgWidth = imgHeight * aspectRatio;
                                }
                                const x = (imageObj.idxNumber % numCols) * cellWidth + horizontalMargin;
                                const y = Math.floor(imageObj.idxNumber / numCols) * cellHeight + verticalMargin;
                                ctx.drawImage(img, x, y, imgWidth, imgHeight);
                                resolve();
                            };
                            img.src = URL.createObjectURL(imageObj.blob);
                        });
                    });

                    Promise.all(imageLoadPromises).then(() => {
                        canvas.toBlob(blob => {
                            fileData.push({
                                blob: blob,
                                fileName: `_for_social_ads/flyer_page_${pageNumber}.png`,
                                fileType: 'image/png'
                            });
                            resolve();
                        }, 'image/png');
                    });
                });
            }));
        })
        .then(() => {
            // Continue with the existing PDF generation and upload logic
            const horizontalMargin = 15;
            const verticalMargin = 20;

            // const pageWidth = pdf.internal.pageSize.getWidth();
            // const pageHeight = pdf.internal.pageSize.getHeight();
        
            const pageWidth = 210;
            const pageHeight = 280;
            
            let p = Promise.resolve();
            fileData.sort((a, b) => {
                if (a.pageNumber === b.pageNumber) {
                    return a.idxNumber - b.idxNumber;
                }
                return a.pageNumber - b.pageNumber;
            });

            let currentRow = 0;
            let currentCol = 0;
            let currentPage = -1;
            let currentRowMinHeight = 0;

            fileData.forEach((fileObj, absoluteIndex) => {
                if (fileObj.fileType === 'image/png' && fileObj.pageNumber !== undefined) {
                    if (fileObj.pageNumber !== currentPage) {
                        currentPage = fileObj.pageNumber;
                        currentRow = 0;
                        currentCol = 0;
                        currentRowMinHeight = 0;
                    }

                    const currentPageDimensions = gridDimensions[fileObj.pageNumber];
                    const numCols = currentPageDimensions ? parseInt(currentPageDimensions.split('x')[1]) : 3;
                    const numRows = 4;
                    const cellWidth = (pageWidth - 2 * horizontalMargin) / numCols;
                    const cellHeight = (pageHeight - 2 * verticalMargin) / numRows;

                    p = p.then(() => {
                        return new Promise((resolve, reject) => {
                            const img = new Image();
                            img.onload = function() {
                                const aspectRatio = this.width / this.height;
                                let imgWidth, imgHeight;

                                if (this.width > this.height) {
                                    imgWidth = cellWidth * grids[fileObj.pageNumber][fileObj.idxNumber].spanWidth;
                                    imgHeight = imgWidth / aspectRatio;
                                } else {
                                    imgHeight = cellHeight * grids[fileObj.pageNumber][fileObj.idxNumber].spanHeight;
                                    imgWidth = imgHeight * aspectRatio;
                                }

                                const x = currentCol * cellWidth + horizontalMargin;
                                const y = currentRow * cellHeight + (1.5)*verticalMargin;

                                const reader = new FileReader();
                                reader.readAsDataURL(fileObj.blob);
                                reader.onloadend = function() {
                                    const base64data = reader.result;
                                    pdf.addImage(base64data, 'PNG', x, y, imgWidth, imgHeight);

                                    if (currentCol === 0) {
                                      currentRowMinHeight = grids[fileObj.pageNumber][fileObj.idxNumber].spanHeight;
                                  } else {
                                      currentRowMinHeight = Math.min(currentRowMinHeight, grids[fileObj.pageNumber][fileObj.idxNumber].spanHeight);
                                  }
                                  currentCol = Math.floor(fileObj.idxNumber % numCols) +  grids[fileObj.pageNumber][fileObj.idxNumber].spanWidth;
                                  if (currentCol >= numCols) {
                                      currentRow += currentRowMinHeight;
                                      currentCol = 0;
                                  }
        
                                  if (currentRow >= numRows && absoluteIndex !== imageUrls.length - 1) {
                                      pdf.addPage();
                                      currentRow = 0;
                                      currentCol = 0;
                                  }
                                    resolve();
                                };
                            };
                            img.onerror = function(e) {
                                console.error("Failed to load image with URL:", img.src);
                                reject(e);
                            };
                            img.src = URL.createObjectURL(fileObj.blob);
                        });
                    });
                }
            });

            return p;
        })
        .then(() => {
            const pdfBlob = pdf.output('blob');
            fileData.push({
                blob: pdfBlob,
                fileName: `_for_web/final_flyer.pdf`,
                fileType: 'application/pdf'
            });

            // Adjusting fileData for cell images to be in '_for_digital_ads' subfolder
            fileData.forEach(fileObj => {
              if (fileObj.fileType === 'image/png' && !fileObj.fileName.startsWith('_for_social_ads')) {
                fileObj.fileName = `_for_digital_ads/${fileObj.fileName}`;
              }
            });

            return uploadFiles(fileData);
        }).then((uploadedFilesData) => {
          // Use uploadedFilesUrls here.
          const pdfData = uploadedFilesData.find(data => data.originalName.includes('final_flyer.pdf'));

          if (!pdfData) {
              throw new Error('PDF URL not found in uploaded files');
          }

          createdFlyer.pdf_url = pdfData.url;

          // Save per-page image URLs so the store website can show all pages
          const pageImages = uploadedFilesData
            .filter(data => data.originalName.includes('_for_social_ads/flyer_page_'))
            .sort((a, b) => {
              const pageA = parseInt(a.originalName.match(/flyer_page_(\d+)/)?.[1] || '0');
              const pageB = parseInt(b.originalName.match(/flyer_page_(\d+)/)?.[1] || '0');
              return pageA - pageB;
            })
            .map(data => data.url);
          createdFlyer.images = pageImages;

          return saveFlyer(false);
        })
        .then(() => {
            setIsExporting(false);
            hideOverlay();
        })
        .catch((error) => {
            console.error("Error uploading:", error);
            setIsExporting(false);
            hideOverlay();
        });
  };

  const deleteFlyer = async () => {
    try {
      const token = await getAccessTokenSilently();
      const flyerId = createdFlyer._id || createdFlyer.flyer_id; // MongoDB uses _id
      await axios.delete(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyers/${flyerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // After successful deletion, navigate to the /flyers page
      navigate('/flyers');
    } catch (error) {
      notification.error('Error occurred while deleting the flyer.');
    }
  };

  const updateLayerRefs = useCallback((combinedIndex, ref) => {
    const validIndexPattern = /^\d+-\d+$/;
      if (!validIndexPattern.test(combinedIndex)) {
          console.warn(`Invalid ref index: ${combinedIndex}`);
          return;
      }
    // Update both state and ref - ref is used during export to get latest values
    shapesLayerRefsRef.current[combinedIndex] = ref;
    setShapesLayerRefs(prevRefs => ({
        ...prevRefs,
        [combinedIndex]: ref,
    }));
  }, []);

  // Replace a product in an enhanced cell by index
  const replaceCellProduct = useCallback((cellIndex, productIndex, newProduct) => {
    setGrids(prevGrids => {
      const [pageStr, cellStr] = cellIndex.split('-');
      const pageNum = parseInt(pageStr);
      const cellIdx = parseInt(cellStr);
      const updatedGrids = [...prevGrids];
      const currentGrid = [...updatedGrids[pageNum]];
      const cellObj = currentGrid[cellIdx];
      if (cellObj?.products) {
        const newProducts = [...cellObj.products];
        newProducts[productIndex] = newProduct;
        currentGrid[cellIdx] = { ...cellObj, products: newProducts };
        updatedGrids[pageNum] = currentGrid;
      }
      return updatedGrids;
    });
  }, []);

  // Auto-fill products into empty cells.
  // Reads gridsRef for current state (avoids stale closure), writes via setGrids.
  // Returns the number of products actually placed.
  const autoFillProducts = useCallback((products) => {
    if (!products || products.length === 0) return 0;

    const currentGrids = gridsRef.current;
    let filledCount = 0;

    // Sort products so similar items end up in the same multi-product cell:
    // 1. Group by category
    // 2. Within same category, sort by price proximity
    // 3. No category → group by common words in product name
    const getPrice = (p) => parseFloat(p.promoPrice) || parseFloat(p.originalPrice) || 0;
    const getCategory = (p) => (p.category || '').toLowerCase().trim();
    const getNameWords = (p) => new Set((p.name || '').toLowerCase().split(/\s+/).filter(w => w.length > 2));

    const sorted = [...products].sort((a, b) => {
      const catA = getCategory(a);
      const catB = getCategory(b);

      // Same category → sort by price
      if (catA && catB && catA === catB) return getPrice(a) - getPrice(b);

      // Both have categories → group alphabetically
      if (catA && catB) return catA.localeCompare(catB);

      // No category → group by name similarity (common words count, descending)
      if (!catA && !catB) {
        const wordsA = getNameWords(a);
        const wordsB = getNameWords(b);
        const shared = [...wordsA].filter(w => wordsB.has(w)).length;
        if (shared > 0) return getPrice(a) - getPrice(b); // similar names → sort by price
      }

      // Products with category come first
      if (catA && !catB) return -1;
      if (!catA && catB) return 1;

      return getPrice(a) - getPrice(b);
    });

    const productQueue = sorted;

    // Diagnostic: log grid state at time of auto-fill
    const allCells = currentGrids.flat();
    const nonNullCells = allCells.filter(c => c !== null);
    const enhancedCells = nonNullCells.filter(c => isEnhancedCell(c));
    const legacyCells = nonNullCells.filter(c => !isEnhancedCell(c) && c.content && Array.isArray(c.content));
    console.log('[AUTO-FILL] Grid state:', {
      pages: currentGrids.length,
      totalCells: allCells.length,
      nonNullCells: nonNullCells.length,
      enhancedCells: enhancedCells.length,
      legacyCells: legacyCells.length,
      sampleCell: nonNullCells[0] ? {
        hasStaticShapes: !!nonNullCells[0].static_shapes,
        hasSharedShapes: !!nonNullCells[0].shared_shapes,
        hasProductBlock: !!nonNullCells[0].product_block,
        hasContent: !!nonNullCells[0].content,
        existingProducts: nonNullCells[0].products?.length || 0,
      } : 'NO CELLS',
    });

    const updatedGrids = currentGrids.map((grid, pageIdx) => {
      if (productQueue.length === 0) return grid;
      let gridChanged = false;
      const updatedGrid = grid.map((cell, cellIdx) => {
        if (!cell || productQueue.length === 0) return cell;

        // Enhanced cells — skip cells not flagged as product cells (e.g. headers/banners)
        if (isEnhancedCell(cell)) {
          if (cell.is_product_cell === false) return cell;
          const maxProducts = 1;
          const existing = cell.products || [];
          const available = maxProducts - existing.length;
          if (available <= 0) return cell;

          const toAdd = productQueue.splice(0, available);
          filledCount += toAdd.length;
          gridChanged = true;
          return { ...cell, products: [...existing, ...toAdd] };
        }

        // Legacy cells: update content shapes
        if (cell.content && Array.isArray(cell.content)) {
          const hasLinkedProducts = cell.content.some(s => s.linkedProductProperty);
          if (!hasLinkedProducts) return cell;

          const PLACEHOLDER_NAMES = new Set([
            'product name', 'product name1', 'product name 1',
            'product name2', 'product name 2',
            'name', 'item name', 'your product', 'sample product',
          ]);
          const hasExistingProduct = cell.content.some(
            s => s.linkedProductProperty === 'product_name' && s.text && s.text.trim() !== '' &&
              !PLACEHOLDER_NAMES.has(s.text.trim().toLowerCase())
          );
          if (hasExistingProduct) return cell;

          const product = productQueue.shift();
          if (!product) return cell;
          filledCount++;

          const newContent = cell.content.map(shape => {
            if (!shape.linkedProductProperty) return shape;
            const updated = { ...shape };
            switch (shape.linkedProductProperty) {
              case 'product_name':
                updated.text = product.name || '';
                break;
              case 'promo_price_dollars':
                updated.text = product.promoPrice ? `${Math.floor(product.promoPrice)}` : `${Math.floor(product.originalPrice || 0)}`;
                break;
              case 'promo_price_cents': {
                const price = product.promoPrice || product.originalPrice || 0;
                const [, cents] = Number(price).toFixed(2).split('.');
                updated.text = cents;
                break;
              }
              case 'original_price':
                updated.text = product.originalPrice ? product.originalPrice.toString() : '';
                break;
              case 'price_unit':
                updated.text = product.priceUnit || '';
                break;
              case 'product_description':
                updated.text = product.productDescription || '';
                break;
              case 'savings':
                if (product.originalPrice && product.promoPrice) {
                  updated.text = `Save $${(product.originalPrice - product.promoPrice).toFixed(2)}`;
                }
                break;
              case 'limited_qty':
                updated.text = product.limit_qty || '';
                break;
              case 'promo_type':
                updated.text = product.promoType || '';
                break;
              default:
                break;
            }
            return updated;
          });

          // Handle image shapes
          const imageShapes = newContent.filter(s => s.type === 'image' && s.linkedProductProperty);
          imageShapes.forEach(shape => {
            let imageSrc = null;
            if (shape.linkedProductProperty === 'cleaned_img_large_src') {
              imageSrc = product.cleanedImgLargeSrc || product.originalImgLargeSrc;
            } else if (shape.linkedProductProperty === 'original_img_large_src') {
              imageSrc = product.originalImgLargeSrc;
            }
            if (imageSrc) {
              shape.imageUrl = imageSrc;
            }
          });

          gridChanged = true;
          return { ...cell, content: newContent };
        }

        return cell;
      });
      return gridChanged ? updatedGrid : grid;
    });

    console.log('[AUTO-FILL] Result:', {
      filledCount,
      inputProducts: products.length,
      remainingInQueue: productQueue.length,
    });

    // Log a sample updated cell to verify products were set
    if (filledCount > 0) {
      const sampleCell = updatedGrids.flat().find(c => c && c.products?.length > 0);
      if (sampleCell) {
        console.log('[AUTO-FILL] Sample filled cell:', {
          products: sampleCell.products.map(p => ({ name: p.name, promoPrice: p.promoPrice, originalPrice: p.originalPrice })),
          isEnhanced: isEnhancedCell(sampleCell),
          hasProductBlock: !!sampleCell.product_block,
        });
      }
    }

    if (filledCount > 0) {
      setGrids(updatedGrids);
    }

    return filledCount;
  }, []);

  /**
   * Export flyer assets (page images + PDF) as base64 for server-side use.
   * Reuses the same Konva rendering + canvas compositing as publishFlyer,
   * but returns data instead of uploading to S3.
   * Used by WhatsApp flow via FlyerPreview?autoExport=true
   */
  const exportForServer = async () => {
    setIsExporting(true);

    // Wait for all pages to render
    const expectedCellCount = grids.reduce((total, grid) => {
      if (!grid) return total;
      return total + grid.filter(cell => cell !== null).length;
    }, 0);

    await new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 50;
      const checkRefs = () => {
        const renderedGridItems = document.querySelectorAll('.grid-item');
        if (renderedGridItems.length >= expectedCellCount || attempts >= maxAttempts) {
          setTimeout(resolve, 200);
        } else {
          attempts++;
          setTimeout(checkRefs, 100);
        }
      };
      setTimeout(checkRefs, 100);
    });

    const { fileData } = await generateImageData(shapesLayerRefsRef.current);

    // Composite cell images into page images (same logic as publishFlyer)
    const pages = {};
    fileData.forEach(fileObj => {
      if (!pages[fileObj.pageNumber]) pages[fileObj.pageNumber] = [];
      pages[fileObj.pageNumber].push(fileObj);
    });

    const pageImageBlobs = await Promise.all(
      Object.entries(pages).map(([pageNumber, pageImages]) => {
        return new Promise((resolve) => {
          pageImages.sort((a, b) => a.idxNumber - b.idxNumber);

          const currentPageDimensions = gridDimensions[pageNumber];
          const numCols = currentPageDimensions ? parseInt(currentPageDimensions.split('x')[1]) : 3;
          const pageWidth = BASE_EXPORT_WIDTH * EXPORT_RESOLUTION_MULTIPLIER;
          const pageHeight = BASE_EXPORT_HEIGHT * EXPORT_RESOLUTION_MULTIPLIER;
          const horizontalMargin = BASE_HORIZONTAL_MARGIN * EXPORT_RESOLUTION_MULTIPLIER;
          const verticalMargin = BASE_VERTICAL_MARGIN * EXPORT_RESOLUTION_MULTIPLIER;
          const numRows = 4;
          const cellWidth = (pageWidth - 2 * horizontalMargin) / numCols;
          const cellHeight = (pageHeight - 2 * verticalMargin) / numRows;

          const canvas = document.createElement('canvas');
          canvas.width = pageWidth;
          canvas.height = pageHeight;
          const ctx = canvas.getContext('2d');

          const imageLoadPromises = pageImages.map(imageObj => {
            return new Promise((imgResolve) => {
              const img = new Image();
              img.onload = () => {
                const aspectRatio = img.width / img.height;
                let imgWidth, imgHeight;
                if (img.width > img.height) {
                  imgWidth = cellWidth * grids[imageObj.pageNumber][imageObj.idxNumber].spanWidth;
                  imgHeight = imgWidth / aspectRatio;
                } else {
                  imgHeight = cellHeight * grids[imageObj.pageNumber][imageObj.idxNumber].spanHeight;
                  imgWidth = imgHeight * aspectRatio;
                }
                const x = (imageObj.idxNumber % numCols) * cellWidth + horizontalMargin;
                const y = Math.floor(imageObj.idxNumber / numCols) * cellHeight + verticalMargin;
                ctx.drawImage(img, x, y, imgWidth, imgHeight);
                imgResolve();
              };
              img.src = URL.createObjectURL(imageObj.blob);
            });
          });

          Promise.all(imageLoadPromises).then(() => {
            canvas.toBlob(blob => {
              resolve({ pageNumber: parseInt(pageNumber), blob });
            }, 'image/png');
          });
        });
      })
    );

    // Generate PDF (same logic as publishFlyer)
    const pdf = new jsPDF('p', 'mm', 'a4');
    const horizontalMargin = 15;
    const verticalMargin = 20;
    const pageWidth = 210;
    const pageHeight = 280;

    const sortedFileData = [...fileData]
      .filter(f => f.pageNumber !== undefined)
      .sort((a, b) => a.pageNumber === b.pageNumber ? a.idxNumber - b.idxNumber : a.pageNumber - b.pageNumber);

    let currentRow = 0;
    let currentCol = 0;
    let currentPage = -1;
    let currentRowMinHeight = 0;

    for (let i = 0; i < sortedFileData.length; i++) {
      const fileObj = sortedFileData[i];
      if (fileObj.fileType !== 'image/png') continue;

      if (fileObj.pageNumber !== currentPage) {
        if (currentPage >= 0) pdf.addPage();
        currentPage = fileObj.pageNumber;
        currentRow = 0;
        currentCol = 0;
        currentRowMinHeight = 0;
      }

      const currentPageDimensions = gridDimensions[fileObj.pageNumber];
      const numCols = currentPageDimensions ? parseInt(currentPageDimensions.split('x')[1]) : 3;
      const numRows = 4;
      const cellW = (pageWidth - 2 * horizontalMargin) / numCols;
      const cellH = (pageHeight - 2 * verticalMargin) / numRows;

      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = function () {
          const aspectRatio = this.width / this.height;
          let imgWidth, imgHeight;
          if (this.width > this.height) {
            imgWidth = cellW * grids[fileObj.pageNumber][fileObj.idxNumber].spanWidth;
            imgHeight = imgWidth / aspectRatio;
          } else {
            imgHeight = cellH * grids[fileObj.pageNumber][fileObj.idxNumber].spanHeight;
            imgWidth = imgHeight * aspectRatio;
          }

          const x = currentCol * cellW + horizontalMargin;
          const y = currentRow * cellH + 1.5 * verticalMargin;

          const reader = new FileReader();
          reader.readAsDataURL(fileObj.blob);
          reader.onloadend = function () {
            pdf.addImage(reader.result, 'PNG', x, y, imgWidth, imgHeight);

            if (currentCol === 0) {
              currentRowMinHeight = grids[fileObj.pageNumber][fileObj.idxNumber].spanHeight;
            } else {
              currentRowMinHeight = Math.min(currentRowMinHeight, grids[fileObj.pageNumber][fileObj.idxNumber].spanHeight);
            }
            currentCol = Math.floor(fileObj.idxNumber % numCols) + grids[fileObj.pageNumber][fileObj.idxNumber].spanWidth;
            if (currentCol >= numCols) {
              currentRow += currentRowMinHeight;
              currentCol = 0;
            }
            if (currentRow >= numRows && i !== sortedFileData.length - 1) {
              pdf.addPage();
              currentRow = 0;
              currentCol = 0;
            }
            resolve();
          };
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(fileObj.blob);
      });
    }

    // Convert everything to base64
    const pageImagesBase64 = await Promise.all(
      pageImageBlobs
        .sort((a, b) => a.pageNumber - b.pageNumber)
        .map(({ blob }) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.readAsDataURL(blob);
          });
        })
    );

    // pdf.output('datauristring') returns "data:application/pdf;base64,<base64data>"
    // Extract just the base64 portion
    const pdfDataUri = pdf.output('datauristring');
    const pdfBase64 = pdfDataUri.split(',')[1] || btoa(pdf.output());

    setIsExporting(false);

    return {
      pageImages: pageImagesBase64,
      pdf: pdfBase64,
    };
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    saveFlyer,
    downloadFlyer,
    deleteFlyer,
    publishFlyer,
    replaceCellProduct,
    autoFillProducts,
    exportForServer,
  }));

  const renderMainContent = () => {
    // Determine which grids to render - all or just the current page
    // When exporting, render ALL pages so their refs are available for image generation
    const gridsToRender = (currentPageIndex !== null && !isExporting)
      ? [{ grid: grids[currentPageIndex], index: currentPageIndex }]
      : grids.map((grid, index) => ({ grid, index }));

    return (
      <main>

        <div className="div-block" style={{alignContent: 'center' }}>
          <div className="flyer-canvas" style={{width: ((CELL_SIZE * 3) + (20*3))}}>
          {gridsToRender.map(({ grid, index: gridIndex }) => {
            if (!grid) return null;
            let gridCellSize;
            let cols;
            if(gridDimensions[gridIndex] === '4x4') {
              gridCellSize = 75;
              cols = 4;
            } else {
              gridCellSize = 100;
              cols = 3;
            }
            const gridWidth = gridCellSize * cols;
            // Cell height is based on template dimensions (400) * reduction factor (0.25) = 100
            const cellHeight = 400 * CELL_SIZE_REDUCTION_FACTOR; // = 100
            const gridHeight = cellHeight * 4; // 4 rows = 400
            // During export, hide non-current pages visually but keep them in DOM for ref access
            const isHiddenDuringExport = isExporting && currentPageIndex !== null && gridIndex !== currentPageIndex;
            return (
              <div
                className="flyer-grid"
                style={{
                  ...style,
                  position: isHiddenDuringExport ? 'absolute' : 'relative',
                  left: isHiddenDuringExport ? '-9999px' : undefined,
                  visibility: isHiddenDuringExport ? 'hidden' : 'visible'
                }}
                key={gridIndex}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(' + cols + ', 1fr)',
                    columnGap:'0',
                    rowGap: '0',
                    border: '1px dashed #ccc',
                    width: gridWidth
                  }}
                >
                    {grid.map((cell, cellIndex) => {
                    const combinedIndex = `${gridIndex}-${cellIndex}`;
                    return (
                      cell ? (
                      <GridItem
                        key={combinedIndex}
                        index={combinedIndex}
                        cell={cell.content}
                        cellSize={gridCellSize}
                        cell_width={cell.dimensions.width * CELL_SIZE_REDUCTION_FACTOR}
                        cell_height={cell.dimensions.height * CELL_SIZE_REDUCTION_FACTOR}
                        spanWidth={cell.spanWidth}
                        spanHeight={cell.spanHeight}
                        originalTemplateWidth={cell.templateWidth}
                        originalTemplateHeight={cell.templateHeight}
                        assignTemplate={assignTemplate}
                        handleCellUpdate={handleCellUpdate}
                        nextProductSlot={nextProductSlots[gridIndex]}
                        updateLayerRefs={updateLayerRefs}
                        flyerData={createdFlyer}
                        cellMode={cell.cellMode}
                        overrideType={cell.overrideType}
                        overrideContent={cell.overrideContent}
                        onCellOverride={handleCellOverride}
                        cellData={cell}
                        selectedCellProduct={selectedCellProduct}
                        onSelectCellProduct={onSelectCellProduct}
                        onDeleteCellProduct={handleDeleteCellProduct}
                      />
                    ) : null
                    );
                  })}
                </div>
                {/* Center Guidelines */}
                {showCenterGuides && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: gridWidth,
                    height: gridHeight,
                    pointerEvents: 'none',
                    zIndex: 100
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      right: 0,
                      height: '1.5px',
                      background: 'repeating-linear-gradient(to right, #ff8264 0, #ff8264 6px, transparent 6px, transparent 10px)'
                    }} />
                    <div style={{
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      bottom: 0,
                      width: '1.5px',
                      background: 'repeating-linear-gradient(to bottom, #ff8264 0, #ff8264 6px, transparent 6px, transparent 10px)'
                    }} />
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>

      </main>
    )
  };

  return renderMainContent();
});

export default Flyer;
